import {createElement, ReactElement, ReactNode} from "react";
import {
    RenderableDocument,
    DibElementChild,
    DibJsonNode,
    DibObject,
    RenderAugmentation,
    RenderModifier,
    RenderRule,
    RenderNodeOptions,
} from "./types";

type RenderNodeFactoryArgs = {
    ruleset?: RenderRule[];
    augmentations?: RenderAugmentation[];
    modifiers?: RenderModifier[];
};

const VOID_TAGS = new Set([
    "area",
    "base",
    "br",
    "col",
    "command",
    "embed",
    "hr",
    "input",
    "keygen",
    "link",
    "meta",
    "param",
    "source",
    "track",
    "wbr",
]);

// Hack for å erstatte *N* i ren tekst med tellerverdien. Se "Opplæringsplan nyansatt - mal"
// Fungerer ved å sende inn options.replaceN: true fra dokumentgenerator
const replaceNVarStar = (text: string, options?: RenderNodeOptions) => {
    return options?.replaceN ? text.replace("*N*", String(options?.n)) : text;
};

const getNode = (topicData: RenderableDocument, id: string): DibJsonNode => {
    const element = topicData.elements[id] || null;

    // Reference the real object if object.type === "pointer-x-var"
    let obj = topicData.objects?.[id] || null;
    if (obj?.type === "pointer-x-var") {
        obj = topicData.objects?.[obj.data.varId] || null;
    }

    const elementData = topicData.elementdata?.[id] || null;

    // 1. Determine "kind"
    const imgKind = topicData.elements?.[id]?.name === "img" ? "image" : null;
    // 1.1 Slightly hacky, but there is a mismatch between the element.attributes.class
    //     and object.data.type in some cases. If the object has the endPoint property,
    //     it is coming from legacy resource data, so we can ignore it
    const objectKind =
        obj?.type === "linkdata" && !("endPoint" in obj.data)
            ? obj.data.type
            : null;

    const kind =
        imgKind || objectKind || element?.attributes?.class || obj?.type;

    return {kind, element, object: obj, elementData};
};

const checkExemptFromStyleStripping = (name: string): boolean => {
    switch (name) {
        case "table":
        case "tbody":
        case "col":
        case "colgroup":
        case "tr":
        case "td":
        case "ul":
        case "ol":
            return true;
        default:
            return false;
    }
};

const renamedAttributes: {[key: string]: string} = {
    class: "className",
    colspan: "colSpan",
    rowspan: "rowSpan",
    "xml:space": "xmlSpace",
    align: "data-align",
};

const strippedAttributes = [
    "exId",
    "onclick",
    "serialnumber",
    "valign",
    "style",
    "cType",
];

const validStyles = [
    "list-style-type",
    "text-align",
    "border-collapse",
    "width",
];

const styleParsers = {
    ["list-style-type"]: (listStyleType: string) => {
        return {
            listStyleType: listStyleType,
        };
    },
    ["text-align"]: (textAlignType: string) => {
        return {
            textAlign: textAlignType,
        };
    },
    ["border-collapse"]: (borderCollapse: string) => {
        return {
            borderCollapse: borderCollapse,
        };
    },
    ["width"]: (width: string) => {
        return {
            width: width,
        };
    },
};

const parsedAttributes: Record<
    string,
    (
        value: string,
        exemptFromStripping: boolean
    ) => Record<string, string> | null
> = {
    style: (value, exemptFromStripping) => {
        // Separate style attributes into key/value pairs
        const styles = value.split(";").reduce(
            (accumulator, current) => {
                if (!current) {
                    return accumulator;
                }
                const [key, value] = current.split(":");
                return {
                    ...accumulator,
                    [key.trim()]: value.trim(),
                };
            },
            {} as Record<string, string>
        );

        const parsedInlineStyles = exemptFromStripping
            ? validStyles.reduce((accumulator, current) => {
                  if (
                      styles[current] &&
                      styleParsers[current as keyof typeof styleParsers]
                  ) {
                      return {
                          ...accumulator,
                          ...styleParsers[current as keyof typeof styleParsers](
                              styles[current]
                          ),
                      };
                  }
                  return accumulator;
              }, {})
            : {};

        // Should return a react Style object

        if (Object.keys(parsedInlineStyles).length === 0) {
            return null;
        }
        return parsedInlineStyles;
    },
};

const fixAttributes = (
    jsonAttr: Record<string, string>,
    exemptFromStripping = false
) => {
    let reactAttr: {
        [key: string]: string | number;
    } = {};

    try {
        reactAttr = Object.keys(jsonAttr).reduce(
            (accumulator, currentValue) => {
                if (renamedAttributes[currentValue]) {
                    return {
                        ...accumulator,
                        [renamedAttributes[currentValue]]:
                            jsonAttr[currentValue],
                    };
                }
                // Skriv om inline style (style="list-style-type: disc") til noe React forstår.
                // Tillat kun gitte stilattributter.
                // F.eks: style="border: none"; -> style={{border: "none"}}
                if (parsedAttributes[currentValue]) {
                    return {
                        ...accumulator,
                        [currentValue]: parsedAttributes[currentValue](
                            jsonAttr[currentValue],
                            exemptFromStripping
                        ),
                    };
                }

                if (strippedAttributes.indexOf(currentValue) !== -1) {
                    return accumulator;
                }

                if (
                    currentValue === "target" &&
                    jsonAttr[currentValue] === "_blank"
                ) {
                    return {
                        ...accumulator,
                        [currentValue]: jsonAttr[currentValue],
                        rel: "noopener noreferrer",
                    };
                }

                return {
                    ...accumulator,
                    [currentValue]: jsonAttr[currentValue],
                };
            },
            {}
        );
    } catch (err) {
        console.error("Error parsing attributes", jsonAttr);
        console.error(err);
    }

    return reactAttr;
};

const matchNodeToComponent = ({
    node,
    map,
    ruleset = [],
    options = {},
    key,
    children,
}: {
    node: DibJsonNode;
    map: RenderableDocument;
    ruleset: RenderRule[];
    options: RenderNodeOptions;
    key: string;
    children: ReactNode[];
}) => {
    const component = ruleset
        .find((r) => r.shouldParse(node, map, options))
        ?.parser(node, options, {children, map}, key);

    return component;
};

const getObjectDataAttributes = (objData: DibObject["data"]) => {
    const convertedAttributes: Record<
        string,
        (value: unknown) => Record<string, string>
    > = {
        autoCount: (value) => {
            if (value === true) {
                return {
                    "data-autocount": "true",
                };
            }
            return {} as Record<string, string>;
        },
    };

    return Object.keys(objData).reduce(
        (accumulator, current) => {
            const converter = convertedAttributes[current];
            if (converter) {
                return {
                    ...accumulator,
                    ...converter(objData[current as keyof typeof objData]),
                };
            }
            return accumulator;
        },
        {} as Record<string, string>
    );
};

const getElementAttributes = (node: DibJsonNode) => {
    const nodeObjDataAttrs = node.object?.data
        ? getObjectDataAttributes(node.object.data)
        : {};
    const nodeElementAttrs = fixAttributes(
        node.element?.attributes ?? {},
        checkExemptFromStyleStripping(node.element?.name ?? "")
    );
    return {
        ...nodeElementAttrs,
        ...nodeObjDataAttrs,
    } as Record<string, string>;
};

const RenderNodeFactory = ({
    ruleset = [] as RenderRule[],
    augmentations = [] as RenderAugmentation[],
    modifiers = [] as RenderModifier[],
}: RenderNodeFactoryArgs) => {
    const renderNode = (
        topicData: RenderableDocument,
        id: string,
        options: RenderNodeOptions,
        key?: string
    ): ReactNode => {
        if (!topicData || !id) {
            return null;
        }

        if (options?.skip?.(id)) {
            return null;
        }

        let conditionalClassName = "";

        if (options?.isConditional?.(id)) {
            conditionalClassName = "view-conditional";
        }

        if (options?.isConditionalFocused?.(id)) {
            conditionalClassName = "view-conditional--focused";
        }

        const node = getNode(topicData, id);
        if (!node) {
            return null;
        }

        // Preface: Hvis noden har attributes.class og denne inneholder dib-x-hide, så skal noden ikke rendres
        const nodeClassName = node.element?.attributes?.class ?? "";

        if (nodeClassName.includes("dib-x-hide")) {
            return null;
        }

        // 2. Loop gjennom node.element.children og kjør dem inn i renderNode
        const children = (node.element?.children || []).map((child) => {
            if ("text" in child) {
                return replaceNVarStar(child.text, options);
            }

            return renderNode(topicData, child.id, options);
        });

        // 3. Test noden opp mot augmentations og generer en modifisert children-array
        const augmentedChildren = augmentations?.reduce((acc, curr) => {
            if (curr.test(node, options)) {
                return curr.processor(node, topicData, acc);
            }
            return acc;
        }, children);

        // 4. Modifiser noden opp mot modifiers og generer en modifisert node
        const activeModifiers =
            modifiers?.filter((m) => m.test(node, options)) || [];

        const component = matchNodeToComponent({
            node,
            map: topicData,
            ruleset: ruleset || [],
            options: {
                ...options,
                conditionalClassName,
                rootTopicId: topicData.topicId,
            },
            key: key ?? id,
            children: augmentedChildren,
        });

        if (component) {
            let ret = component as ReactElement;

            if (activeModifiers.length > 0) {
                ret = activeModifiers.reduce((acc, curr) => {
                    return curr.modifier(node, acc) as ReactElement;
                }, ret);
            }

            return ret;
        }

        /**
         * Hvis vi ikke har funnet noe komponent som matcher og noden ikke har element, så er det noe galt.
         * Dette kan tyde å feil fra dibadmin, eller at vi har glemt å legge til en regel for å håndtere
         * komponenten.
         */
        if (node.element === null || !node.element.attributes) {
            console.error(
                `Cancelling rendering. Node ${id} does not match any component and is not a valid element:`,
                node
            );
            return null;
        }

        // 5. Fallback for elementer som ikke matcher. Dvs. vanlige html-elementer.
        const elementAttributes = getElementAttributes(node);

        // 5.1 Merge class names
        if (conditionalClassName) {
            elementAttributes.className = `${elementAttributes.className ?? ""} ${conditionalClassName}`;
        }

        // 5.2 CSS target, probably legacy
        if (options?.CSSTarget && id === options?.CSSTarget) {
            elementAttributes["data-target"] = "true";
        }

        // 5.2 Render the HTML element
        let nativeElement: ReactElement | null = null;

        if (VOID_TAGS.has(node.element.name?.toLowerCase())) {
            nativeElement = createElement(node.element.name, {
                ...elementAttributes,
                key: key || id,
            });
        } else {
            nativeElement = createElement(
                node.element.name,
                {
                    ...elementAttributes,
                    key: key || id,
                },
                augmentedChildren
            );
        }

        if (!nativeElement) {
            return null;
        }

        // 5.4 Run modifiers on the native element
        activeModifiers.forEach((modifier) => {
            nativeElement = modifier.modifier(node, nativeElement!);
        });

        return nativeElement;
    };
    /* renderChildren: Helper for rendering children that might not have an ID */
    const renderChildren = (
        topicData: RenderableDocument,
        children: DibElementChild[],
        options: {
            skip?: (id: string) => boolean;
        }
    ): ReactNode[] => {
        if (!children || children.length === 0) return [];

        return children.map((child) => {
            if ("text" in child) {
                return child.text;
            }
            if (options?.skip?.(child.id)) {
                return [] as ReactNode[];
            }
            return renderNode(topicData, child.id, options, child.id);
        });
    };

    /* For some cases we want to only render plain text regardless of actual content */
    const renderText = (topicData?: RenderableDocument, id?: string): string => {
        if (!topicData || !id) {
            return "";
        }

        const node = getNode(topicData, id);

        if (!node) {
            return "";
        }

        const text = (node.element?.children || ([] as DibElementChild[])).map(
            (child) => {
                if ("text" in child) {
                    return child.text;
                }
                return renderText(topicData, child.id);
            }
        );
        return text.join(" ");
    };

    return {renderNode, renderChildren, renderText};
};

export default RenderNodeFactory;
